import React, { useMemo, forwardRef } from 'react';
import { Card, Box, Typography } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { BASE_OPTIONS, EJE_X_BASE, EJE_Y_BASE, formatoValorPct } from './chartBase';
import { COLOR, FUENTE } from './tokens';
import InfoGrafico from './InfoGrafico';
import { MOCK_ESTADOS_TURNO } from './mockDataEjemplos';

const COLOR_ESTADO = {
    FINALIZADO: COLOR.verde,
    ASISTIO: COLOR.verdeClaro,
    NO_ASISTIO: COLOR.rojo,
    CANCELADO: COLOR.grisFrio,
};
const ESTADOS_ATENDIDOS = new Set(['FINALIZADO', 'ASISTIO']);

/**
 * Resultado de los turnos — barras horizontales por estado, reemplaza la torta de
 * 7 gajos (con 7 categorías la torta no permite comparar gajos).
 * @param {{data: Array<{estado: string, cantidad: number}>}} props
 */
const EstadosTurnoChart = forwardRef(({ data, asistencia = null, compacto = false }, ref) => {
    const filas = useMemo(() => [...(data || [])].sort((a, b) => b.cantidad - a.cantidad), [data]);
    const total = useMemo(() => filas.reduce((acc, f) => acc + f.cantidad, 0), [filas]);

    // Las cuentas del pie son las mismas barras sumadas, sin porcentajes propios: el único porcentaje es
    // la Asistencia del encabezado (`asistencia`), que va sobre otra base y se aclara. Antes el pie
    // calculaba su propio "% del total" (contando cancelados) y daba un tercer número para lo mismo.
    const { atendidos, noVinieron, cancelados } = useMemo(() => {
        const suma = (esEstado) => filas.filter((f) => esEstado(f.estado)).reduce((a, f) => a + f.cantidad, 0);
        return {
            atendidos: suma((e) => ESTADOS_ATENDIDOS.has(e)),
            noVinieron: suma((e) => e === 'NO_ASISTIO'),
            cancelados: suma((e) => e === 'CANCELADO'),
        };
    }, [filas]);

    const chartData = {
        labels: filas.map((f) => f.estado.replaceAll('_', ' ')),
        datasets: [{
            data: filas.map((f) => f.cantidad),
            backgroundColor: filas.map((f) => COLOR_ESTADO[f.estado] || COLOR.grisFrio),
            borderRadius: 6,
            barThickness: 22,
        }],
    };

    const options = {
        ...BASE_OPTIONS,
        indexAxis: 'y',
        plugins: {
            ...BASE_OPTIONS.plugins,
            legend: { display: false },
            tooltip: {
                ...BASE_OPTIONS.plugins.tooltip,
                callbacks: {
                    label: (ctx) => formatoValorPct(ctx.raw, total ? (ctx.raw / total) * 100 : 0),
                },
            },
        },
        scales: {
            x: { ...EJE_Y_BASE, ticks: { ...EJE_Y_BASE.ticks, precision: 0 } },
            y: { ...EJE_X_BASE, grid: { display: false } },
        },
    };

    return (
        <Card variant="outlined" sx={{
            position: compacto ? 'static' : 'relative', border: compacto ? 'none' : '1px solid #e2e8f0',
            borderRadius: compacto ? 0 : '12px', boxShadow: 'none', padding: compacto ? 0 : '20px 22px',
        }}>
            {!compacto && (
                <>
                    <InfoGrafico
                        titulo="Resultado de los turnos"
                        queMuestra="Te dice, de los turnos del período que ya tienen resultado, cuántos terminaron bien (el cliente vino y se lo atendió), cuántos se perdieron porque no vino, y cuántos se cancelaron antes de la fecha. Los que siguen abiertos todavía no tienen resultado y no aparecen."
                        ejemploVisual={<EstadosTurnoChart compacto data={MOCK_ESTADOS_TURNO} />}
                        comoLeerlo={[
                            'Cada barra es un estado posible del turno, no una serie que se pueda comparar entre sí — el orden va de mejor a peor resultado.',
                            'Verde (Finalizado) = el turno terminó con el cliente atendido de punta a punta.',
                            'Verde claro (Asistió) = el cliente vino, pero la atención quedó incompleta o a mitad de camino.',
                            'Rojo (No asistió) = el cliente no se presentó al turno agendado.',
                            'Gris (Cancelado) = el turno se dio de baja antes de la fecha, nunca llegó a intentarse.',
                            "Debajo se cuentan los que se presentaron (Finalizado + Asistió), los que no vinieron y los que cancelaron. La Asistencia que se lee ahí es la del encabezado: se calcula solo sobre los turnos que vinieron o faltaron, así que los cancelados no entran.",
                        ]}
                        analisis="En este ejemplo, 'No asistió' (25) es casi tan grande como 'Finalizado' (42) — un ausentismo así de alto generalmente no se arregla mejorando la atención en el local, sino revisando cómo se confirman los turnos antes de la fecha (un recordatorio más cercano al horario, doble confirmación por el canal que más ausentismo tenga). 'Cancelado' (10) es un problema distinto: ahí el cliente avisó a tiempo, así que ese turno se podría haber reasignado a otra persona en lista de espera en vez de perderse."
                    />
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 16, fontWeight: 700, color: COLOR.negro }}>Resultado de los turnos</Typography>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 12.5, color: COLOR.grisTexto, marginBottom: '14px' }}>
                        Cantidad de turnos por estado final del período.
                    </Typography>
                </>
            )}
            <Box sx={{ height: compacto ? 160 : 240 }}>
                <Bar ref={ref} data={chartData} options={options} />
            </Box>
            {!compacto && total > 0 && (
                <Box sx={{ marginTop: '14px' }}>
                    <Typography sx={{ fontFamily: FUENTE, fontSize: 13, color: COLOR.negro }}>
                        Se presentaron <b style={{ color: COLOR.verde }}>{atendidos.toLocaleString('es-AR')}</b>
                        {' · '}No vinieron <b style={{ color: COLOR.rojo }}>{noVinieron.toLocaleString('es-AR')}</b>
                        {' · '}Cancelaron <b>{cancelados.toLocaleString('es-AR')}</b>
                    </Typography>
                    {asistencia != null && (
                        <Typography sx={{ fontFamily: FUENTE, fontSize: 12, color: COLOR.grisTexto, marginTop: '4px' }}>
                            Asistencia <b style={{ color: COLOR.negro }}>{Number(asistencia).toFixed(1)}%</b>: de los turnos que se
                            resolvieron (vinieron o faltaron), sin cancelados — la misma cifra del encabezado.
                        </Typography>
                    )}
                </Box>
            )}
        </Card>
    );
});

export default EstadosTurnoChart;
